/**
 * Scanner-Schwarzweiss im Browser -- Gegenstueck zu `Backend/src/utils/imageEnhance.js`.
 *
 * Der Ablauf ist derselbe wie dort, damit die Vorschau im Viewer dem entspricht,
 * was beim Speichern serverseitig herauskommt: Graustufen, Beleuchtungsausgleich
 * gegen einen weichgezeichneten Hintergrund und ein Schwellwert nach Otsu.
 */

/** Schwellwert nach Otsu aus einem 256-Bin-Histogramm. */
export const otsuThreshold = (histogram: Uint32Array, totalPixels: number): number => {
  let sumAll = 0;
  for (let i = 0; i < 256; i += 1) sumAll += i * histogram[i];

  let sumBackground = 0;
  let weightBackground = 0;
  let best = 0;
  let bestVariance = -1;

  for (let t = 0; t < 256; t += 1) {
    weightBackground += histogram[t];
    if (weightBackground === 0) continue;
    const weightForeground = totalPixels - weightBackground;
    if (weightForeground === 0) break;

    sumBackground += t * histogram[t];
    const meanBackground = sumBackground / weightBackground;
    const meanForeground = (sumAll - sumBackground) / weightForeground;
    const variance = weightBackground * weightForeground * (meanBackground - meanForeground) ** 2;

    if (variance > bestVariance) {
      bestVariance = variance;
      best = t;
    }
  }

  return best;
};

/** Kastenweichzeichner (zwei Durchlaeufe) auf einem Graustufen-Array. */
const boxBlur = (source: Uint8ClampedArray, width: number, height: number, radius: number) => {
  const horizontal = new Float32Array(source.length);
  const result = new Float32Array(source.length);

  for (let y = 0; y < height; y += 1) {
    let sum = 0;
    const row = y * width;
    for (let x = -radius; x <= radius; x += 1) {
      sum += source[row + Math.min(width - 1, Math.max(0, x))];
    }
    for (let x = 0; x < width; x += 1) {
      horizontal[row + x] = sum / (radius * 2 + 1);
      const outgoing = source[row + Math.min(width - 1, Math.max(0, x - radius))];
      const incoming = source[row + Math.min(width - 1, Math.max(0, x + radius + 1))];
      sum += incoming - outgoing;
    }
  }

  for (let x = 0; x < width; x += 1) {
    let sum = 0;
    for (let y = -radius; y <= radius; y += 1) {
      sum += horizontal[Math.min(height - 1, Math.max(0, y)) * width + x];
    }
    for (let y = 0; y < height; y += 1) {
      result[y * width + x] = sum / (radius * 2 + 1);
      const outgoing = horizontal[Math.min(height - 1, Math.max(0, y - radius)) * width + x];
      const incoming = horizontal[Math.min(height - 1, Math.max(0, y + radius + 1)) * width + x];
      sum += incoming - outgoing;
    }
  }

  return result;
};

/**
 * Erzeugt aus einer Bildquelle ein sauberes Schwarzweiss-Dokument.
 * @param source geladenes Bild
 * @param invert Farben am Ende umkehren
 * @returns Data-URL (PNG) oder null, wenn kein Canvas-Kontext verfuegbar ist
 */
export const renderScannerBw = (source: HTMLImageElement, invert = false): string | null => {
  const width = source.naturalWidth;
  const height = source.naturalHeight;
  if (!width || !height) return null;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return null;

  context.drawImage(source, 0, 0);
  const imageData = context.getImageData(0, 0, width, height);
  const pixels = imageData.data;

  const gray = new Uint8ClampedArray(width * height);
  for (let i = 0, p = 0; i < pixels.length; i += 4, p += 1) {
    // Dieselbe Luminanzgewichtung wie sharp/libvips.
    gray[p] = (pixels[i] * 0.299 + pixels[i + 1] * 0.587 + pixels[i + 2] * 0.114) | 0;
  }

  const radius = Math.max(8, Math.round(Math.min(width, height) / 25));
  const background = boxBlur(gray, width, height, radius);

  const corrected = new Uint8ClampedArray(gray.length);
  const histogram = new Uint32Array(256);
  for (let p = 0; p < gray.length; p += 1) {
    const paper = background[p] || 1;
    const value = Math.min(255, Math.round((gray[p] / paper) * 245));
    corrected[p] = value;
    histogram[value] += 1;
  }

  const threshold = otsuThreshold(histogram, gray.length);
  for (let p = 0, i = 0; p < corrected.length; p += 1, i += 4) {
    let value = corrected[p] > threshold ? 255 : 0;
    if (invert) value = 255 - value;
    pixels[i] = value;
    pixels[i + 1] = value;
    pixels[i + 2] = value;
    pixels[i + 3] = 255;
  }

  context.putImageData(imageData, 0, 0);
  return canvas.toDataURL('image/png');
};
